/**
 * The dev preview, as a hook: where the reel is, and how to step it.
 *
 * One place reads the preview store (`#/lib/moduleFixtures`), so the switcher at
 * the top of a post's header, the card in a feed and the module's own page cannot
 * disagree about which case is being drawn — and so that pressing an arrow
 * *redraws*, which is the whole point of a switcher.
 *
 * ## Why the reel is held here, and the rest of it is read
 *
 * Two very different kinds of answer come out of this hook, and they want opposite
 * treatment:
 *
 *  - **Whether the sheet is on** (`cases`) is a *fact about the store*, written by
 *    a chip on the card as often as by the reel, so it is read from storage — in
 *    the render that draws it, never memoised. A memo keyed on a version counter
 *    was the first version of this hook, and it froze: the card below the switcher
 *    changed case on every press while the label above it kept naming the case it
 *    had mounted with.
 *  - **Where the reel is** (`previewCase`) is chosen by pressing an arrow *in the
 *    switcher*, so it is held as state in the component that draws it. The store is
 *    told, because every other surface reads it there; but the label does not wait
 *    for a subscription to come back around to move, because the one thing a
 *    switcher must never do is sit still while the thing it switched has changed.
 *
 * A third answer is neither: **whether preview mode is on at all** (`mode`) is a
 * stored fact of the same kind as the sheet's flag, read fresh in the render that
 * draws it — so turning the mode off in the settings screen takes the reel away
 * under the reader's hands, and turning it back on puts the case they had back
 * (`#/lib/moduleFixtures`).
 *
 * The subscription is kept for the cases that are chosen *elsewhere* — a
 * `?preview=` link, the chip that stops the sheet, another surface's reel — so a
 * switcher on screen always catches up with the truth, wherever it was set. And it
 * redraws on **every** write, not only on the ones that move the reel: the case is
 * the only thing here that is state, and the chip's own write is precisely the one
 * that leaves it where it is (*show all cases* pressed from off the reel puts
 * `null` over `null`), so a subscriber that only set the case went on drawing the
 * sheet it had just been told to stop drawing — the chip could turn the deck on and
 * never off. The extra number below is not a snapshot and nothing is keyed on it;
 * it is permission to draw again, and every value the redraw reads is read fresh
 * from the store in that render.
 *
 * Nothing here is reachable outside `IS_DEV`: the hook still subscribes and still
 * holds its state in a production build (hooks cannot be conditional), but every
 * value it hands back is the honest one — no case, no spec sheet.
 */
import {useCallback, useEffect, useState} from 'react'

import {
  applyModulePreviewCase,
  clearModulePreview,
  MODULE_PREVIEW_CASES,
  readModuleCasesPreview,
  readModulePreviewCase,
  readModulePreviewMode,
  subscribeModulePreview,
  type ModulePreviewCase,
  writeModuleCasesPreview,
} from '#/lib/moduleFixtures'
import {IS_DEV} from '#/env'

export function useModulePreviewCase(): {
  /** The case being shown, or `null` when the preview is off the reel. */
  previewCase: ModulePreviewCase | null
  /** Where in the reel it sits, 1-based — `0` when off it, or when preview is off. */
  position: number
  total: number
  /**
   * Whether the preview is on at all — the master switch, off in the settings
   * screen (`#/lib/moduleFixtures`).
   *
   * The reel is drawn by the reader of this value, not by this hook: a card that
   * never showed the switcher cannot forget to stop showing it, and the *facts* are
   * gated in the store either way, so this only has to answer *do we offer the
   * tool*. Off, everything below is the honest nothing. */
  mode: boolean
  /** Whether the card is drawing every rung at once. */
  cases: boolean
  /** The next case, wrapping round at the end. */
  next: () => void
  /** The one before it, wrapping round at the start. */
  prev: () => void
  /** Forget the preview entirely — the reader's own class, seat and run. */
  reset: () => void
  /** The spec sheet on its own, for the chip on the card. */
  toggleCases: () => void
} {
  const [previewCase, setPreviewCase] = useState<ModulePreviewCase | null>(
    () => (IS_DEV ? readModulePreviewCase() : null),
  )
  /* Read by nothing; a write changes it so the render happens (see the header). */
  const [, redraw] = useState(0)

  useEffect(
    () =>
      subscribeModulePreview(() => {
        setPreviewCase(readModulePreviewCase())
        redraw(seen => seen + 1)
      }),
    [],
  )

  const mode = IS_DEV ? readModulePreviewMode() : false
  const cases = IS_DEV ? readModuleCasesPreview() : false

  const step = useCallback((delta: number) => {
    const index = MODULE_PREVIEW_CASES.findIndex(
      one => one.id === readModulePreviewCase()?.id,
    )
    /*
     * Off the reel, › starts it and ‹ lands on the last case — the two directions
     * mean the same thing (somewhere in the list) from either edge.
     */
    const from = index < 0 ? (delta > 0 ? -1 : 0) : index
    const nextIndex =
      (from + delta + MODULE_PREVIEW_CASES.length) % MODULE_PREVIEW_CASES.length
    const chosen = MODULE_PREVIEW_CASES[nextIndex]
    /*
     * The store first, then this component: every other surface reads the store,
     * and this one has the answer in hand.
     */
    applyModulePreviewCase(chosen.id)
    setPreviewCase(chosen)
  }, [])

  const next = useCallback(() => step(1), [step])
  const prev = useCallback(() => step(-1), [step])
  const reset = useCallback(() => {
    clearModulePreview()
    setPreviewCase(null)
  }, [])
  const toggleCases = useCallback(() => {
    writeModuleCasesPreview(!readModuleCasesPreview())
  }, [])

  return {
    previewCase,
    position: previewCase
      ? MODULE_PREVIEW_CASES.findIndex(one => one.id === previewCase.id) + 1
      : 0,
    total: MODULE_PREVIEW_CASES.length,
    mode,
    cases,
    next,
    prev,
    reset,
    toggleCases,
  }
}
